// ============================================================
// TERROR ENGINE — KEYBOARD NAV
// Universal keyboard interaction for modals and menus.
//
// useModalKeys(onClose, opts?)
//   — Escape closes any modal/screen
//   — Enter also triggers close if no options
//   — Optional: Enter triggers a primary action instead
//
// useKeyboardNav(items, onSelect, opts?)
//   — Arrow keys move highlight through a list
//   — Enter activates highlighted item
//   — Returns: { activeIdx, setActiveIdx, itemProps(idx) }
//     itemProps(idx) returns style props to spread on each item
//
// Usage:
//   // Simple close on Escape:
//   useModalKeys(onClose);
//
//   // With highlighted options:
//   const nav = useKeyboardNav(options, (opt) => handlePick(opt));
//   {options.map((opt, i) => (
//     <button {...nav.itemProps(i)} onClick={() => handlePick(opt)}>
//       {opt.label}
//     </button>
//   ))}
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { SD } from "./theme";

// ─────────────────────────────────────────────────────────────
// useModalKeys — Escape closes, optional Enter primary action
// ─────────────────────────────────────────────────────────────
export function useModalKeys(onClose, {
  onEnter   = null,   // if set, Enter triggers this instead of onClose
  enabled   = true,
  delay     = 150,    // ms before keys are active (prevent accidental trigger)
} = {}) {
  const ready = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => { ready.current = true; }, delay);
    return () => { clearTimeout(t); ready.current = false; };
  }, [enabled, delay]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (!ready.current) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (onEnter) onEnter();
        else onClose?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onEnter, enabled]);
}

// ─────────────────────────────────────────────────────────────
// useKeyboardNav — arrow key navigation + Enter selection
//
// items: any[]           — the list being navigated
// onSelect: (item, idx)  — called when Enter pressed on highlighted item
// opts.loop: boolean     — wrap around at ends (default true)
// opts.enabled: boolean  — disable during animations etc (default true)
// opts.startIdx: number  — initial highlight index (default 0)
//
// Returns:
//   activeIdx            — currently highlighted index
//   setActiveIdx         — manually set highlight
//   itemProps(idx)       — spread on each item for highlight + click binding
// ─────────────────────────────────────────────────────────────
export function useKeyboardNav(items, onSelect, {
  loop     = true,
  enabled  = true,
  startIdx = 0,
} = {}) {
  const [activeIdx, setActiveIdx] = useState(startIdx);

  // Clamp activeIdx if items change
  useEffect(() => {
    if (items.length === 0) return;
    setActiveIdx(i => Math.min(i, items.length - 1));
  }, [items.length]);

  useEffect(() => {
    if (!enabled || items.length === 0) return;
    const handler = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setActiveIdx(i => {
          const next = i + 1;
          return loop ? next % items.length : Math.min(next, items.length - 1);
        });
      }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveIdx(i => {
          const prev = i - 1;
          return loop ? (prev < 0 ? items.length - 1 : prev) : Math.max(prev, 0);
        });
      }
      if (e.key === "Enter") {
        e.preventDefault();
        setActiveIdx(i => {
          onSelect?.(items[i], i);
          return i;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, onSelect, loop, enabled]);

  // itemProps — spread onto each list item
  const itemProps = useCallback((idx) => ({
    "data-kb-active": activeIdx === idx,
    onMouseEnter: () => setActiveIdx(idx),
    style: activeIdx === idx ? HIGHLIGHT_STYLE : {},
  }), [activeIdx]);

  return { activeIdx, setActiveIdx, itemProps };
}

// ─────────────────────────────────────────────────────────────
// HIGHLIGHT_STYLE — applied to the active item
// Zones/modals can override via the data-kb-active attribute
// ─────────────────────────────────────────────────────────────
export const HIGHLIGHT_STYLE = {
  outline: `2px solid ${SD.purpleLight}`,
  outlineOffset: "2px",
  boxShadow: `0 0 12px ${SD.purpleLight}66`,
};

// ─────────────────────────────────────────────────────────────
// KeyboardHint — small "↑↓ navigate · Enter select · Esc close"
// Show in any modal that has keyboard nav active
// ─────────────────────────────────────────────────────────────
export function KeyboardHint({ hasNav = true, style = {} }) {
  return (
    <div style={{
      fontSize: 9,
      color: SD.starDim,
      letterSpacing: 1.5,
      textAlign: "center",
      fontFamily: SD.fontDisplay,
      marginTop: 8,
      opacity: 0.7,
      ...style,
    }}>
      {hasNav ? "↑↓ navigate · Enter select · Esc close" : "Esc to close"}
    </div>
  );
}
